/**
 * Citas: la pantalla de recepción.
 *
 * Pensada para el día de cola: campaña de un especialista, veinte personas
 * esperando. Recepción no quiere ver demográficos ni cumplimiento — quiere
 * teclear un DNI, encontrar al paciente y darle su hora. Si no existe, lo
 * registra ahí mismo y sigue de largo a la cita sin volver a buscarlo.
 *
 * La ficha del paciente sigue existiendo para lo otro: ver su historial, sus
 * atenciones, su estado. Son dos trabajos distintos y ahora tienen dos
 * pantallas distintas.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, CalendarPlus, Search, UserPlus, X } from "lucide-react";
import { listCitas, type Cita } from "../api/citas";
import { listPacientes, type Paciente } from "../api/pacientes";
import { getPaciente } from "../api/pacientes";
import { getMiEspacio } from "../api/miEspacio";
import { atenderSolicitud } from "../api/solicitudes";
import { useAuth } from "../auth/AuthProvider";
import { useBase, useZonaHoraria } from "../auth/TenantBrandingProvider";
import { diaEnZona, fechaLarga, hora, hoyEnZona, localAIso } from "../lib/fecha";
import { AgendarCitaDialog } from "../components/atencion/AgendarCitaDialog";
import { EstadoFlujoDeCita } from "../components/atencion/EstadoFlujo";
import { CreatePacienteDialog } from "./PacientesListPage";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Dialog } from "../components/ui";


export function CitasPage() {
  const base = useBase();
  const tz = useZonaHoraria();
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const buscadorRef = useRef<HTMLInputElement | null>(null);

  const [texto, setTexto] = useState("");
  const [paciente, setPaciente] = useState<Paciente | null>(null);
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [elegirOpen, setElegirOpen] = useState(false);
  // La cita recién creada: se salta a su día y se resalta, porque casi nunca
  // es hoy y si no, parecería que no se guardó.
  const [reciente, setReciente] = useState<Cita | null>(null);
  // Se entra a trabajar el día de hoy: esa es la vista por defecto. Pero si
  // HOY no hay nada y sí hay citas por delante, se abre en "Próximas": una
  // pantalla que dice "sin citas ese día" mientras hay gente agendada para
  // mañana obliga a un clic para enterarse de lo que había que ver al entrar.
  //
  // Solo cuando nadie eligió pestaña. En cuanto se toca una, manda esa: no hay
  // nada peor que una pantalla que decide por ti lo que acabas de decidir tú.
  const vistaPedida = params.get("ver") as
    | "hoy"
    | "proximas"
    | "pasadas"
    | "dia"
    | null;
  // El día de hoy, en la zona del policlínico. Va ARRIBA de todo lo que lo
  // usa: declararlo después de las consultas que lo leen deja la página en
  // blanco con un ReferenceError, y eso no lo ve el compilador.
  const hoy = hoyEnZona(tz);

  // Cuántas citas hay más allá de hoy. Sin este número, "Hoy 3" se lee como
  // "solo hay 3 citas" y el que registró una para el martes cree que se perdió.
  const { data: proximas = [] } = useQuery({
    queryKey: ["citas", "cuenta-proximas", hoy],
    queryFn: () => listCitas({ desde: localAIso(hoy, "23:59", tz) }),
  });


  // Hoy, según el propio listado del día.
  const { data: citasHoy = [] } = useQuery({
    queryKey: ["citas", "cuenta-hoy", hoy],
    queryFn: () => listCitas({ fecha: hoy }),
  });

  const vista: "hoy" | "proximas" | "pasadas" | "dia" =
    vistaPedida ?? (citasHoy.length === 0 && proximas.length > 0 ? "proximas" : "hoy");

  // Lo que tiene a alguien esperando. Sale de Mi Espacio, que ya lo calcula:
  // una segunda cuenta aquí sería otro sitio donde se puede desincronizar.
  const { data: espacio } = useQuery({
    queryKey: ["mi-espacio"],
    queryFn: getMiEspacio,
    refetchInterval: 60_000,
  });
  const bandeja = (codigo: string) =>
    espacio?.bandejas.find((b) => b.codigo === codigo);
  const pidenCita = bandeja("solicitudes")?.total ?? 0;
  const porValidar = bandeja("validar_pago")?.total ?? 0;

  // Se llega desde la bandeja "Piden cita": el paciente y la solicitud vienen
  // en la URL. La solicitud se guarda para cerrarla con la cita que salga.
  const solicitudId = Number(params.get("solicitud")) || null;
  const pacienteDeSolicitud = Number(params.get("paciente")) || null;

  const { data: pacienteSolicitud } = useQuery({
    queryKey: ["paciente", pacienteDeSolicitud],
    queryFn: () => getPaciente(pacienteDeSolicitud!),
    enabled: pacienteDeSolicitud != null,
  });

  const cerrarSolicitud = useMutation({
    mutationFn: ({ id, citaId }: { id: number; citaId: number }) =>
      atenderSolicitud(id, citaId),
    onSuccess: () => {
      // Las bandejas y su bolita cuentan esta solicitud: si no se recargan,
      // sigue apareciendo pendiente hasta el siguiente refresco automático.
      queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
    },
  });

  const fecha = params.get("fecha") ?? hoyEnZona(tz);

  const puedeAgendar =
    me?.is_super_admin || (me?.permisos.includes("paciente:update") ?? false);

  // El foco arranca en el buscador: se entra a esta pantalla a teclear un DNI.
  useEffect(() => {
    buscadorRef.current?.focus();
  }, []);

  // Espera a que deje de teclear para no lanzar una búsqueda por letra.
  const [consulta, setConsulta] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setConsulta(texto.trim()), 250);
    return () => clearTimeout(t);
  }, [texto]);

  const { data: encontrados = [], isFetching } = useQuery({
    queryKey: ["pacientes", "buscar", consulta],
    queryFn: () => listPacientes(consulta, 8),
    // Solo hace falta para agendar: quien no puede, no necesita la lista de
    // pacientes, necesita la cita.
    enabled: consulta.length >= 2 && puedeAgendar,
  });

  // Las CITAS de ese paciente, sin importar el día. Es la pregunta real del
  // mostrador —"¿a qué hora tengo mi cita?"— y hasta ahora obligaba a adivinar
  // en qué pestaña estaba: hoy, próximas o pasadas.
  const { data: citasEncontradas = [], isFetching: buscandoCitas } = useQuery({
    queryKey: ["citas", "buscar", consulta],
    queryFn: () => listCitas({ q: consulta, incluir_canceladas: true }),
    enabled: consulta.length >= 2,
  });

  const { data: citas = [], isLoading } = useQuery({
    queryKey: ["citas", "vista", vista, vista === "dia" ? fecha : hoy],
    queryFn: () => {
      if (vista === "proximas")
        return listCitas({ desde: localAIso(hoy, "00:00", tz), incluir_canceladas: true });
      // Pasadas: para auditar lo ya ocurrido — quién vino, quién no, qué se
      // canceló. Sin esto no había forma de mirar hacia atrás.
      if (vista === "pasadas")
        return listCitas({ hasta: localAIso(hoy, "00:00", tz), incluir_canceladas: true });
      return listCitas({
        fecha: vista === "hoy" ? hoy : fecha,
        incluir_canceladas: true,
      });
    },
  });

  // Agrupadas por día: una lista plana de tres semanas no se lee.
  const porDia = citas.reduce<Record<string, Cita[]>>((acc, c) => {
    const d = diaEnZona(c.inicio, tz);
    (acc[d] ??= []).push(c);
    return acc;
  }, {});
  // Hacia atrás se lee del más reciente al más antiguo.
  const dias = Object.keys(porDia).sort();
  if (vista === "pasadas") dias.reverse();

  // Un documento tecleado entero y sin resultados: seguro es paciente nuevo.
  const pareceDocumento = useMemo(
    () => /^\d{8,}$/.test(consulta),
    [consulta],
  );
  const sinResultados =
    consulta.length >= 2 && !isFetching && encontrados.length === 0;

  // De cuál de los dos buscadores viene el documento. Son dos campos distintos
  // —el de la página y el del diálogo de nueva cita— y el formulario tiene que
  // recibir el que se acaba de usar, no siempre el mismo.
  const [buscadoEnDialogo, setBuscadoEnDialogo] = useState("");
  const documentoParaElFormulario = useMemo(() => {
    const candidato = buscadoEnDialogo || consulta;
    return /^\d{8,}$/.test(candidato) ? candidato : undefined;
  }, [buscadoEnDialogo, consulta]);

  function elegir(p: Paciente) {
    setPaciente(p);
    setTexto("");
    setConsulta("");
  }

  return (
    <div className="space-y-6">
      {/* LO PENDIENTE, ANTES QUE NADA.
          Quien entra a Citas no viene a mirar una lista vacía: viene a ver qué
          hay que resolver. Esto sale SIEMPRE, esté en la pestaña que esté, y
          desaparece solo cuando no queda nada. */}
      {(pidenCita > 0 || porValidar > 0) && (
        <div className="flex flex-wrap gap-3">
          {porValidar > 0 && (
            <Link
              to="/mi-espacio"
              className="flex-1 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm hover:bg-amber-100"
            >
              <span className="font-semibold text-amber-900">
                {porValidar} pagaron por la app
              </span>
              <span className="block text-amber-800">
                Subieron su captura y la cita no está confirmada hasta revisarla →
              </span>
            </Link>
          )}
          {pidenCita > 0 && (
            <Link
              to="/mi-espacio"
              className="flex-1 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm hover:bg-sky-100"
            >
              <span className="font-semibold text-sky-900">
                {pidenCita} piden cita
              </span>
              <span className="block text-sky-800">
                Lo pidieron desde su app y esperan tu llamada →
              </span>
            </Link>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <CalendarDays className="h-6 w-6 text-[color:var(--color-link)]" />
            Citas
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Busca al paciente por documento o nombre y dale su hora.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-slate-300">
            {(
              [
                { id: "pasadas", label: "Pasadas", cuenta: null },
                { id: "hoy", label: "Hoy", cuenta: null },
                {
                  id: "proximas",
                  label: "Próximas",
                  cuenta: proximas.length || null,
                },
              ] as const
            ).map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setParams({ ver: v.id }, { replace: true })}
                className={`px-3 py-2 text-sm font-medium transition ${
                  vista === v.id
                    ? "bg-[color:var(--color-link)] text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {v.label}
                {v.cuenta != null && (
                  <span
                    className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                      vista === v.id
                        ? "bg-white/25 text-white"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {v.cuenta}
                  </span>
                )}
              </button>
            ))}
          </div>
          {/* Input nativo y no el componente Input: ese lleva w-full y estiraba
              el campo de fecha a todo el ancho de la fila. */}
          <input
            type="date"
            value={fecha}
            onChange={(e) =>
              setParams({ ver: "dia", fecha: e.target.value }, { replace: true })
            }
            aria-label="Ir a un día concreto"
            className={`w-[9.5rem] rounded-lg border px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 ${
              vista === "dia"
                ? "border-[color:var(--color-link)] bg-white"
                : "border-slate-300 bg-white"
            }`}
          />
          {puedeAgendar && (
            <>
              {/* Agendar era invisible: había que adivinar que se hacía
                  buscando al paciente. Ahora hay un botón que lo dice. */}
              <Button onClick={() => setElegirOpen(true)}>
                <CalendarPlus className="h-4 w-4" />
                Nueva cita
              </Button>
              <Button variant="ghost" onClick={() => setNuevoOpen(true)}>
                <UserPlus className="h-4 w-4" />
                Nuevo paciente
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Buscador: es lo primero y lo más grande de la pantalla, porque es lo
          único que recepción hace veinte veces seguidas. Lo ve todo el que
          entra acá: quien tría también necesita encontrar una cita cuando el
          paciente pregunta, aunque no pueda agendarla. */}
      {(
        <Card>
          <CardBody className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                ref={buscadorRef}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder={
                  puedeAgendar
                    ? "DNI, nombre o apellido…"
                    : "Buscar cita por DNI, nombre o apellido…"
                }
                className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-11 pr-10 text-base text-slate-900 placeholder:text-slate-400 focus:border-[color:var(--color-link)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
                aria-label="Buscar paciente"
              />
              {texto && (
                <button
                  type="button"
                  onClick={() => setTexto("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100"
                  aria-label="Limpiar"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {consulta.length >= 2 && (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {buscandoCitas
                    ? "Buscando citas…"
                    : `${citasEncontradas.length} ${
                        citasEncontradas.length === 1 ? "cita" : "citas"
                      } de «${consulta}»`}
                </p>
                {citasEncontradas.length > 0 && (
                  <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                    {[...citasEncontradas]
                      .sort((a, b) => b.inicio.localeCompare(a.inicio))
                      .slice(0, 8)
                      .map((c) => (
                        <li key={c.id}>
                          <Link
                            to={`${base}/citas/${c.id}`}
                            className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                          >
                            <span className="w-36 shrink-0 font-mono text-xs text-slate-500">
                              {fechaLarga(c.inicio, tz)}
                            </span>
                            <span className="font-mono text-sm text-slate-700">
                              {hora(c.inicio, tz)}
                            </span>
                            <span className="font-medium text-slate-900">
                              {c.paciente_nombre}
                            </span>
                            <span className="text-xs text-slate-500">
                              {c.especialidad_nombre} · {c.profesional_nombre}
                            </span>
                            <EstadoFlujoDeCita cita={c} />
                            <span className="ml-auto text-xs font-medium text-[color:var(--color-link)]">
                              Abrir →
                            </span>
                          </Link>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            )}

            {encontrados.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Pacientes · elige uno para agendarle una cita
                </p>
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {encontrados.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => elegir(p)}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                    >
                      <span className="font-mono text-sm text-slate-500">
                        {p.tipo_documento} {p.num_documento}
                      </span>
                      <span className="font-medium text-slate-900">
                        {p.nombre_completo}
                      </span>
                      <span className="ml-auto text-xs font-medium text-[color:var(--color-link)]">
                        Agendar →
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              </div>
            )}

            {sinResultados && puedeAgendar && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-slate-300 px-4 py-3">
                <span className="text-sm text-slate-600">
                  Nadie con «{consulta}».
                </span>
                <Button variant="ghost" onClick={() => setNuevoOpen(true)}>
                  <UserPlus className="h-4 w-4" />
                  {pareceDocumento
                    ? `Registrar paciente con documento ${consulta}`
                    : "Registrar paciente nuevo"}
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-semibold text-slate-900">
              {vista === "hoy"
                ? "Hoy"
                : vista === "proximas"
                  ? "Próximas citas"
                  : vista === "pasadas"
                    ? "Citas pasadas"
                    : "Ese día"}
            </h2>
            <span className="text-sm text-slate-500">
              {vista === "proximas"
                ? "De hoy en adelante"
                : vista === "pasadas"
                  ? "Antes de hoy, de lo más reciente hacia atrás"
                  : fechaLarga(`${vista === "hoy" ? hoy : fecha}T12:00:00Z`, tz)}
            </span>
            <span className="ml-auto rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-700">
              {citas.length}
            </span>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && (
            <p className="px-6 py-8 text-center text-sm text-slate-400">Cargando…</p>
          )}
          {!isLoading && citas.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-slate-500">
              <p>
                {vista === "proximas"
                  ? "No hay citas programadas de hoy en adelante."
                  : vista === "pasadas"
                    ? "No hay citas anteriores a hoy."
                    : "Sin citas ese día."}
              </p>
              {/* Un día sin citas no es el final del camino: si hay gente
                  agendada más adelante, se dice cuántos y se entra de un clic
                  en vez de dejar la pantalla muda. */}
              {vista !== "proximas" && proximas.length > 0 && (
                <button
                  type="button"
                  onClick={() => setParams({ ver: "proximas" })}
                  className="mt-2 block w-full font-medium text-[color:var(--color-link)] hover:underline"
                >
                  Hay {proximas.length} cita{proximas.length === 1 ? "" : "s"} por
                  delante · verlas →
                </button>
              )}
              {/* Un día vacío no significa que no haya nada que mirar: las
                  atenciones ya hechas están en "Pasadas", y ahí es donde se
                  entra a revisar lo atendido. Sin este empujón la pantalla
                  parecía decir que no existe nada. */}
              {vista !== "pasadas" && (
                <button
                  type="button"
                  onClick={() => setParams({ ver: "pasadas" })}
                  className="mt-2 font-medium text-[color:var(--color-link)] hover:underline"
                >
                  Ver citas pasadas y atenciones ya hechas →
                </button>
              )}
            </div>
          )}
          {dias.map((d) => (
            <div key={d}>
              {/* Cabecera de día: en "Próximas" hace falta saber de cuál es
                  cada bloque; en un solo día sobra y no se pinta. */}
              {vista !== "hoy" && vista !== "dia" && (
                <p className="border-y border-slate-100 bg-slate-50 px-6 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {fechaLarga(`${d}T12:00:00Z`, tz)}
                  {d === hoy && " · hoy"}
                </p>
              )}
              <ul className="divide-y divide-slate-100">
                {porDia[d].map((c) => (
                  <Fila
                    key={c.id}
                    cita={c}
                    base={base}
                    tz={tz}
                    nueva={c.id === reciente?.id}
                  />
                ))}
              </ul>
            </div>
          ))}
        </CardBody>
      </Card>

      {!puedeAgendar && (
        <Alert variant="info">
          Puedes ver la agenda del día, pero agendar citas requiere permiso de
          recepción.
        </Alert>
      )}

      {/* Elegir paciente abre directamente el diálogo de agendar. */}
      <AgendarCitaDialog
        pacienteId={paciente?.id ?? pacienteSolicitud?.id ?? 0}
        pacienteNombre={paciente?.nombre_completo ?? pacienteSolicitud?.nombre_completo}
        open={paciente != null || pacienteSolicitud != null}
        onClose={() => {
          setPaciente(null);
          // Se sale sin agendar: la solicitud sigue pendiente, así que se
          // quita de la URL para no volver a abrir el diálogo al navegar.
          if (solicitudId) setParams({ ver: "proximas" }, { replace: true });
        }}
        onCreated={(c) => {
          setReciente(c);
          // La petición se cierra apuntando a ESTA cita: es lo que le enseña
          // al paciente, en su app, que lo que pidió ya tiene hora.
          if (solicitudId) cerrarSolicitud.mutate({ id: solicitudId, citaId: c.id });
          // Que la cita recién creada quede a la vista, sea del día que sea.
          setParams({ ver: "proximas" }, { replace: true });
        }}
      />

      {elegirOpen && (
        <ElegirPacienteDialog
          onClose={() => setElegirOpen(false)}
          onElegido={(p) => {
            setElegirOpen(false);
            setPaciente(p);
          }}
          onNuevo={(buscado) => {
            // Lo que se escribió DENTRO del diálogo, que es otro buscador
            // distinto al de la página. Sin esto, quien tecleaba el DNI aquí
            // lo encontraba vacío en el formulario y lo escribía dos veces.
            setBuscadoEnDialogo(buscado);
            setElegirOpen(false);
            setNuevoOpen(true);
          }}
        />
      )}

      <CreatePacienteDialog
        open={nuevoOpen}
        onClose={() => setNuevoOpen(false)}
        documentoInicial={documentoParaElFormulario}
        // Recién registrado, va derecho a su cita: no se vuelve a buscar.
        onCreated={(p) => {
          setTexto("");
          setConsulta("");
          setPaciente(p);
        }}
      />
    </div>
  );
}

function Fila({
  cita: c,
  base,
  tz,
  nueva,
}: {
  cita: Cita;
  base: string;
  tz: string;
  nueva?: boolean;
}) {
  return (
    <li
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-6 py-3 ${
        nueva ? "bg-[color-mix(in_srgb,var(--color-link)_8%,white)]" : ""
      }`}
    >
      <span className="w-16 shrink-0 font-mono text-sm text-slate-500">
        {hora(c.inicio, tz)}
      </span>
      <Link
        to={`${base}/citas/${c.id}`}
        className="font-medium text-slate-900 hover:text-[color:var(--color-link)] hover:underline"
      >
        {c.paciente_nombre}
      </Link>
      <span className="text-sm text-slate-500">
        {c.especialidad_nombre} · {c.profesional_nombre}
      </span>
      {/* El estado del FLUJO, no el de la cita: dice si ya pagó y si ya pasó
          por triaje, que es lo que preguntan en el mostrador. */}
      <EstadoFlujoDeCita cita={c} conPasos />
      {nueva && <Badge variant="success">Recién agendada</Badge>}
      <Link
        to={`${base}/citas/${c.id}`}
        className="ml-auto text-sm font-medium text-[color:var(--color-link)] hover:underline"
      >
        Abrir →
      </Link>
    </li>
  );
}

/**
 * ¿A quién se le agenda? Se pregunta antes de abrir el formulario de la cita.
 *
 * Es el mismo buscador de siempre, pero como paso explícito: antes había que
 * saber que "agendar" se hacía buscando al paciente en la pantalla, y quien no
 * lo sabía concluía —con razón— que desde Citas no se podía agendar.
 */
function ElegirPacienteDialog({
  onClose,
  onElegido,
  onNuevo,
}: {
  onClose: () => void;
  onElegido: (p: Paciente) => void;
  /** Lo que se estaba buscando, para no tener que teclearlo otra vez. */
  onNuevo: (buscado: string) => void;
}) {
  const [texto, setTexto] = useState("");
  const [consulta, setConsulta] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setConsulta(texto.trim()), 250);
    return () => clearTimeout(t);
  }, [texto]);

  const { data: encontrados = [], isFetching } = useQuery({
    queryKey: ["pacientes", "buscar", consulta],
    queryFn: () => listPacientes(consulta, 8),
    enabled: consulta.length >= 2,
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Nueva cita"
      description="¿Para quién es? Busca por DNI, nombre o apellido."
    >
      <div className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            autoFocus
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="DNI, nombre o apellido…"
            className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-base focus:border-[color:var(--color-link)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
          />
        </div>

        {consulta.length >= 2 && !isFetching && encontrados.length === 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-slate-300 px-4 py-3">
            <span className="text-sm text-slate-600">Nadie con «{consulta}».</span>
            <Button variant="ghost" onClick={() => onNuevo(consulta)}>
              <UserPlus className="h-4 w-4" />
              {/* Se nombra el documento en el botón: quien lo ve sabe que no
                  va a tener que volver a teclearlo. */}
              {/^\d{8,}$/.test(consulta)
                ? `Registrar paciente con documento ${consulta}`
                : "Registrar paciente nuevo"}
            </Button>
          </div>
        )}

        {encontrados.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {encontrados.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onElegido(p)}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                >
                  <span className="font-mono text-sm text-slate-500">
                    {p.num_documento ? `${p.tipo_documento} ${p.num_documento}` : "sin documento"}
                  </span>
                  <span className="font-medium text-slate-900">{p.nombre_completo}</span>
                  <span className="ml-auto text-xs font-medium text-[color:var(--color-link)]">
                    Agendar →
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
